import { track } from '@/common/track';
import { INDUSTRY_STANDARD_MATCHES_TAB } from '@/constants';
import { StandardsLibraryProvider } from '@/contexts/StandardsLibraryContext';
import { useAppMutation } from '@/hooks/useAppMutation';
import {
  uploadJobDescriptionErrorKey,
  useUploadJobDescription,
} from '@/hooks/useUploadJobDescription';
import { IIndustryStandardMatches, ISuccessImportJob } from '@/interfaces/Job';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import DragIcon from '@/resources/images/icons-drag.svg';
import RemoveIcon from '@/resources/images/icons-remove.svg';
import { KLAYO_COLORS } from '@/themes';
import { UploadJobDescriptionSchema, UploadJobDescriptionType } from '@/utilities/validationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { JobBuilderEvent } from 'klayowebshared';
import { DragEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Placeholder } from '../../components/Placeholder';
import ImportJobCopyDialog from '../settings/ImportJobCopyDialog';
import IndustryStandardMatches from '../settings/IndustryStandardMatches';
import { StandardMatchedTasksAndCompetenciesRef } from '../settings/StandardMatchedTasksAndCompetencies';
import { DescriptionButtonConfig, JobBuilderSectionRef } from './jobBuilder.types';

type Step = 'upload' | 'matches';

interface Props {
  onButtonConfigChange?: (config: DescriptionButtonConfig) => void;
  /** True while this section is the visible build mode. The parent keeps all sections mounted and
   *  toggles them with display:none; used to remount the matches tables on re-show (see tableNonce). */
  isActive?: boolean;
}

export const JobBuilderDescriptionSection = forwardRef<JobBuilderSectionRef, Props>(
  ({ onButtonConfigChange, isActive }, ref) => {
    const { translate } = useLocale();
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const tasksRef = useRef<StandardMatchedTasksAndCompetenciesRef | null>(null);
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const simulatedProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [step, setStep] = useState<Step>('upload');
    const [matchedData, setMatchedData] = useState<IIndustryStandardMatches | null>(null);
    const [activeTab, setActiveTab] = useState(INDUSTRY_STANDARD_MATCHES_TAB.JOBS);
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
    const [hasTasksSelection, setHasTasksSelection] = useState(false);
    const [showImportJobCopy, setShowImportJobCopy] = useState(false);
    const [isImportingTasks, setIsImportingTasks] = useState(false);

    // The parent hides inactive build modes with display:none, which collapses the matches
    // DataGrid to zero width; its flex columns (task/competency name) don't recover on re-show.
    // Bump a nonce whenever this section becomes visible again to remount the tables fresh — a
    // fresh mount always measures correctly. Selection state lives above the tables and is passed
    // as a controlled rowSelectionModel, so the remount preserves the user's selection.
    const [tableNonce, setTableNonce] = useState(0);
    useEffect(() => {
      if (isActive) setTableNonce((n) => n + 1);
    }, [isActive]);
    const { mutateAsync: upload } = useUploadJobDescription();

    const { mutateAsync: importJob, isPending: isImporting } = useAppMutation<
      AxiosResponse<ISuccessImportJob>,
      any,
      {
        jobDefinitionId: string;
        taskIds: string[];
        competencyIds: string[];
        isOverriding?: boolean;
      }
    >('post', '/StandardLibrary/jobs/import');

    const handleImport = async (isOverriding = false) => {
      if (!selectedJobId) return;
      track(JobBuilderEvent.SelectJdMatchedJobsImport, {
        selection: matchedData?.jobDefinitions?.find((j: any) => j.id === selectedJobId)?.name,
      });
      try {
        const response = await importJob({
          jobDefinitionId: selectedJobId,
          taskIds: [],
          competencyIds: [],
          isOverriding,
        });
        if (response?.data?.isSuccess) {
          showSnackbar(translate('messages.jobImportSuccess'), 'success');
          navigate(`/settings/jobs/edit/${response.data.jobDefinition.jobDefinitionId}`);
        } else {
          showSnackbar(response.data.errorMessage || translate('messages.jobImportError'), 'error');
        }
        setShowImportJobCopy(false);
      } catch (error: any) {
        if (error?.status === 409 || error?.response?.status === 409) {
          setShowImportJobCopy(true);
        } else {
          showSnackbar(
            error?.response?.data?.message || translate('messages.jobImportError'),
            'error'
          );
        }
      }
    };

    const { control, formState, handleSubmit, setValue, watch } = useForm<UploadJobDescriptionType>(
      {
        resolver: zodResolver(UploadJobDescriptionSchema),
        defaultValues: { file: null },
        mode: 'onChange',
      }
    );

    const uploadedFile = watch('file');

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (step === 'matches') {
          if (activeTab === INDUSTRY_STANDARD_MATCHES_TAB.TASKS_AND_COMPETENCIES) {
            track(JobBuilderEvent.SelectJdImport, {
              numTasks: tasksRef.current?.getSelectedTaskIds().length ?? 0,
              numComps: tasksRef.current?.getSelectedCompetencyIds().length ?? 0,
            });
            setIsImportingTasks(true);
            try {
              await tasksRef.current?.triggerImport();
            } finally {
              setIsImportingTasks(false);
            }
            return '';
          }
          await handleImport();
          return '';
        }

        track(JobBuilderEvent.SelectJdContinue);
        await handleSubmit(async (values) => {
          setIsUploading(true);
          setProgress(0);
          try {
            const startSimulation = () => {
              if (simulatedProgressRef.current) return;
              simulatedProgressRef.current = setInterval(() => {
                setProgress((prev) => Math.min(prev + 2, 98));
              }, 1000);
            };
            const stopSimulation = () => {
              if (!simulatedProgressRef.current) return;
              clearInterval(simulatedProgressRef.current);
              simulatedProgressRef.current = null;
            };
            const response = await upload({
              data: values.file,
              onUploadProgress: (event) => {
                const { total, loaded } = event;
                if (!total || !loaded) return;
                const p = Math.min(Math.floor((loaded / total) * 100), 90);
                setProgress(p);
                // All bytes sent — start simulating server-side processing
                if (p >= 90) startSimulation();
              },
            });
            stopSimulation();
            setProgress(100);
            setMatchedData(response.data);
            setStep('matches');
            track(JobBuilderEvent.SelectJdMatchedJobsViewed);
          } catch (error) {
            if (simulatedProgressRef.current) {
              clearInterval(simulatedProgressRef.current);
              simulatedProgressRef.current = null;
            }
            setProgress(0);
            showSnackbar(translate(uploadJobDescriptionErrorKey(error)), 'error');
          } finally {
            setIsUploading(false);
          }
        })();

        return '';
      },
    }));

    useEffect(() => {
      const config: DescriptionButtonConfig = (() => {
        if (step === 'upload') {
          return {
            label: translate('jobBuilder.continue'),
            disabled: !uploadedFile || !!formState.errors.file || isUploading,
            loading: isUploading,
          };
        }
        if (activeTab === INDUSTRY_STANDARD_MATCHES_TAB.JOBS) {
          return {
            label: translate('jobBuilder.importAndEdit'),
            disabled: !selectedJobId || isImporting,
            loading: isImporting,
          };
        }
        return {
          label: translate('standardsLibrary.importAndContinue'),
          disabled: !hasTasksSelection || isImportingTasks,
          loading: isImportingTasks,
        };
      })();
      onButtonConfigChange?.(config);
    }, [
      step,
      activeTab,
      selectedJobId,
      hasTasksSelection,
      uploadedFile,
      formState.errors.file,
      isUploading,
      isImporting,
      isImportingTasks,
    ]);

    useEffect(() => {
      return () => {
        if (simulatedProgressRef.current) clearInterval(simulatedProgressRef.current);
      };
    }, []);

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const dropped = Array.from(e.dataTransfer.files ?? [])[0];
      if (dropped) {
        track(JobBuilderEvent.SelectJdFileUploaded);
        setValue('file', dropped, { shouldValidate: true });
      }
    };

    const handleRemoveFile = () => {
      setValue('file', null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    if (step === 'matches' && matchedData) {
      return (
        <StandardsLibraryProvider>
          <IndustryStandardMatches
            theme={null}
            jobs={matchedData.jobDefinitions}
            tasks={matchedData.tasks}
            competencies={matchedData.competencies}
            hideActions
            tableNonce={tableNonce}
            activeTab={activeTab}
            onTabChange={(tab) => {
              if (tab === INDUSTRY_STANDARD_MATCHES_TAB.TASKS_AND_COMPETENCIES) {
                track(JobBuilderEvent.SelectJdTasksCompsViewed);
              }
              setActiveTab(tab);
            }}
            onJobSelectionChange={(jobId) => {
              if (jobId) {
                track(JobBuilderEvent.SelectJdMatchedJobsSelected, {
                  selection: matchedData.jobDefinitions?.find((j: any) => j.id === jobId)?.name,
                });
              }
              setSelectedJobId(jobId);
            }}
            onTasksSelectionChange={(hasSelection, taskCount, competencyCount) => {
              if (taskCount > 0 || competencyCount > 0) {
                track(JobBuilderEvent.SelectJdTasksCompsToggle, {
                  numTasks: taskCount,
                  numComps: competencyCount,
                });
              }
              setHasTasksSelection(hasSelection);
            }}
            tasksRef={tasksRef}
          />
          <ImportJobCopyDialog
            open={showImportJobCopy}
            loading={isImporting}
            onConfirm={() => handleImport(true)}
            onClose={() => setShowImportJobCopy(false)}
            theme={null}
          />
        </StandardsLibraryProvider>
      );
    }

    return (
      <Stack height={'100%'} flex={1}>
        {isUploading ? (
          <Box
            height='100%'
            display='flex'
            justifyContent='center'
            alignItems='center'
            flexDirection='column'
            flex={1}
          >
            <Box>
              <Box display='flex' alignItems='center' gap={1} flex={1} width='100%'>
                <Tooltip title={`${progress}%`} arrow>
                  <LinearProgress
                    variant='determinate'
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: KLAYO_COLORS.GreyGR200,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: KLAYO_COLORS.NeonBlueNB300,
                        borderRadius: 4,
                      },
                      flex: 1,
                    }}
                  />
                </Tooltip>
                <Typography sx={{ minWidth: 36 }} variant='body2' color='textSecondary'>
                  {progress}%
                </Typography>
              </Box>
              <div className='klayo-placeholder_text_secondary' style={{ marginTop: '10px' }}>
                {translate('standardsLibrary.processingJobDescription')}
              </div>
            </Box>
          </Box>
        ) : (
          <Box
            height={'100%'}
            className={'klayo-upload' + (formState.errors.file ? ' klayo-upload--error' : '')}
            display={'flex'}
            flexDirection={'column'}
            flex={1}
          >
            <Box
              className='klayo-upload_dropzone'
              onDrop={!uploadedFile ? handleDrop : undefined}
              height='100%'
              padding='0'
              display='flex'
              alignItems='center'
              justifyContent='center'
              borderColor={!uploadedFile ? KLAYO_COLORS.NeonBlueNB200 : KLAYO_COLORS.GreyGR300}
              onDragOver={handleDragOver}
              onClick={!uploadedFile ? () => fileInputRef.current?.click() : undefined}
              sx={{
                backgroundColor: uploadedFile ? KLAYO_COLORS.GreyGR100 : KLAYO_COLORS.WhiteWH900,
                cursor: !uploadedFile ? 'pointer' : 'default',
                mt: 0,
              }}
              flex={1}
            >
              <Placeholder
                text={{
                  primary: (
                    <div
                      className='klayo-placeholder_text_secondary'
                      dangerouslySetInnerHTML={{
                        __html: translate('standardsLibrary.clickToUploadPdf'),
                      }}
                    />
                  ),
                }}
                primaryTextSize='16px'
                secondaryTextSize='14px'
                spacing={2}
                icon={
                  <DragIcon
                    style={{
                      color: uploadedFile ? KLAYO_COLORS.GreyGR400 : KLAYO_COLORS.NeonBlueNB200,
                    }}
                  />
                }
                sx={{ pointerEvents: 'none' }}
              />
            </Box>

            {formState.errors.file && (
              <Box className='klayo-upload_error' sx={{ padding: '8px 0 0 !important' }}>
                {formState.errors.file.message}
              </Box>
            )}

            <Box mt={1} className='klayo-placeholder_text_secondary'>
              {translate('standardsLibrary.maxFileSizeMb')}
            </Box>

            {uploadedFile && (
              <Box className='klayo-upload_chipcontainer'>
                <Chip
                  label={uploadedFile.name}
                  deleteIcon={<RemoveIcon />}
                  onDelete={handleRemoveFile}
                />
              </Box>
            )}

            <Controller
              name='file'
              control={control}
              render={({ field }) => (
                <input
                  type='file'
                  accept='.pdf'
                  ref={fileInputRef}
                  style={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    width: 1,
                  }}
                  onChange={(e) => {
                    const files = e.target.files ?? [];
                    if (!files.length) return;
                    track(JobBuilderEvent.SelectJdFileUploaded);
                    field.onChange(files[0]);
                  }}
                />
              )}
            />
          </Box>
        )}
      </Stack>
    );
  }
);

JobBuilderDescriptionSection.displayName = 'JobBuilderDescriptionSection';
